import { getAuthToken } from "../../../../utilities/requests";

/**
 * Read a non-2xx fetch Response, attempting to extract the server's
 * user-facing message from Rightwork's standard error envelope:
 *   { "system": { "status": <code>, "message": "<detail>" } }
 *
 * The AI generation endpoints (generate_quiz_questions_with_ai +
 * generate_swipe_deck_with_ai) return validation messages here that
 * authors need to see — e.g. "No text content found in selected
 * sections/modules", "Number of cards must be between 1 and 10".
 * Without reading the body, the FE could only show "Request failed
 * with status 400", which hides the actionable detail.
 *
 * Falls back to a generic status-coded message if the body isn't JSON
 * or doesn't follow the envelope (network proxy returning HTML, etc.).
 */
async function _readErrorEnvelope(response) {
  let serverMessage = null;
  try {
    const body = await response.json();
    serverMessage = body?.system?.message || null;
  } catch (_) {
    // Body wasn't JSON — fall through to the generic message.
  }
  const err = new Error(
    serverMessage || `Request failed with status ${response.status}`,
  );
  err.status = response.status;
  return err;
}

/**
 * Stream an SSE response from a RightWork API endpoint via POST.
 *
 * Returns a promise that resolves when the stream completes successfully,
 * or rejects on network/HTTP/SSE errors.
 *
 * @param {string} url      - Base URL of the endpoint
 * @param {object} params   - Request body (sent as JSON)
 * @param {function} onToken  - Called with each token string as it arrives
 * @param {function} onDone   - Called with the done payload {annotations: [...]}
 * @param {function} onError  - Called with an error message string (also rejects)
 */
export async function streamRightworkAPI(url, params, onToken, onDone, onError) {
  const token = getAuthToken();

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Token ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const err = new Error(`Request failed with status ${response.status}`);
    err.status = response.status;
    throw err;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let receivedDone = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    // Keep the last (possibly incomplete) line in the buffer
    buffer = lines.pop();

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const jsonStr = line.slice(6).trim();
      if (!jsonStr) continue;

      let event;
      try {
        event = JSON.parse(jsonStr);
      } catch (parseErr) {
        // Skip malformed JSON lines
        continue;
      }

      if (event.type === "token") {
        onToken(event.text);
      } else if (event.type === "done") {
        receivedDone = true;
        onDone(event);
      } else if (event.type === "error") {
        const errorMsg = event.text || "An unknown error occurred.";
        onError(errorMsg);
        throw new Error(errorMsg);
      }
    }
  }

  // Stream ended without a done event — treat as an error
  if (!receivedDone) {
    throw new Error("Stream ended unexpectedly without completing.");
  }
}

/**
 * Stream an SSE response from the AI quiz-question generator.
 *
 * Event vocabulary (mirrors the backend in
 * lms_info/views/learning_block.py:generate_quiz_questions_with_ai):
 *   {type: "started",  total: N}
 *   {type: "question", index: i, question: {...}}
 *   {type: "done",     count: K}
 *   {type: "error",    text: "..."}
 *
 * @param {string} url       - Endpoint URL
 * @param {object} params    - Request body (sent as JSON)
 * @param {object} callbacks - { onStarted(total), onQuestion(question, index),
 *                              onDone(count), onError(msg), onWarning?(msg) }
 * @param {object} options   - { signal?: AbortSignal } — pass an AbortSignal to
 *                             cancel the stream from the caller (e.g. on modal
 *                             close). The fetch + reader both honor it; this
 *                             function rethrows the resulting AbortError so the
 *                             caller can branch on `err.name === "AbortError"`.
 * Resolves on `done`; rejects on network/HTTP errors, `error` events, or abort.
 */
export async function streamQuizGenerationAPI(url, params, callbacks, options = {}) {
  const { onStarted, onQuestion, onDone, onError, onWarning } = callbacks || {};
  const { signal } = options;
  const token = getAuthToken();

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Token ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
    signal,
  });

  if (!response.ok) {
    throw await _readErrorEnvelope(response);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let receivedDone = false;

  while (true) {
    // Belt-and-suspenders: fetch's abort already rejects reader.read(), but
    // checking here cleanly exits the loop if abort lands between reads.
    if (signal?.aborted) {
      try { await reader.cancel(); } catch (_) { /* noop */ }
      const abortErr = new Error("Aborted");
      abortErr.name = "AbortError";
      throw abortErr;
    }

    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop();

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const jsonStr = line.slice(6).trim();
      if (!jsonStr) continue;

      let event;
      try {
        event = JSON.parse(jsonStr);
      } catch (parseErr) {
        continue;
      }

      if (event.type === "started") {
        onStarted?.(event.total);
      } else if (event.type === "warning") {
        // Non-fatal context-scope notices (unindexed documents,
        // retrieval fallback, truncation) — surface, don't interrupt.
        onWarning?.(event.text);
      } else if (event.type === "question") {
        onQuestion?.(event.question, event.index);
      } else if (event.type === "done") {
        receivedDone = true;
        onDone?.(event.count);
      } else if (event.type === "error") {
        const errorMsg = event.text || "An unknown error occurred.";
        onError?.(errorMsg);
        throw new Error(errorMsg);
      }
    }
  }

  if (!receivedDone) {
    // User-facing message — gets toasted verbatim by the editor's
    // catch handler when the helper rejects without an SSE `error`
    // event. Most common cause is a network drop mid-stream.
    throw new Error("Generation was interrupted. Please try again.");
  }
}

/**
 * Stream an SSE response from the AI swipe-deck card generator.
 *
 * Mirrors streamQuizGenerationAPI's vocabulary with `card` instead of
 * `question`. Backend: lms_info/views/learning_swipe_deck.py:
 * generate_swipe_deck_with_ai.
 *
 *   {type: "started", total: N}
 *   {type: "card",    index: i, card: {...}}
 *   {type: "done",    count: K}
 *   {type: "error",   text: "..."}
 *
 * @param {string} url       - Endpoint URL
 * @param {object} params    - Request body (sent as JSON)
 * @param {object} callbacks - { onStarted(total), onCard(card, index), onWarning?(msg),
 *                              onDone(count), onError(msg) }
 * @param {object} options   - { signal?: AbortSignal } — see
 *                             streamQuizGenerationAPI for details.
 * Resolves on `done`; rejects on network/HTTP errors, `error` events, or abort.
 */
export async function streamSwipeDeckGenerationAPI(url, params, callbacks, options = {}) {
  const { onStarted, onCard, onDone, onError, onWarning } = callbacks || {};
  const { signal } = options;
  const token = getAuthToken();

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Token ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
    signal,
  });

  if (!response.ok) {
    throw await _readErrorEnvelope(response);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let receivedDone = false;

  while (true) {
    if (signal?.aborted) {
      try { await reader.cancel(); } catch (_) { /* noop */ }
      const abortErr = new Error("Aborted");
      abortErr.name = "AbortError";
      throw abortErr;
    }

    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop();

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const jsonStr = line.slice(6).trim();
      if (!jsonStr) continue;

      let event;
      try {
        event = JSON.parse(jsonStr);
      } catch (parseErr) {
        continue;
      }

      if (event.type === "started") {
        onStarted?.(event.total);
      } else if (event.type === "warning") {
        // Non-fatal context-scope notices — surface, don't interrupt.
        onWarning?.(event.text);
      } else if (event.type === "card") {
        onCard?.(event.card, event.index);
      } else if (event.type === "done") {
        receivedDone = true;
        onDone?.(event.count);
      } else if (event.type === "error") {
        const errorMsg = event.text || "An unknown error occurred.";
        onError?.(errorMsg);
        throw new Error(errorMsg);
      }
    }
  }

  if (!receivedDone) {
    // See streamQuizGenerationAPI for rationale — toasted verbatim.
    throw new Error("Generation was interrupted. Please try again.");
  }
}

/**
 * Stream an SSE response from the AI Training Studio outline generator.
 *
 * Event vocabulary (mirrors the backend in
 * lms_info/views/training_generation.py:post_learning_generation_job_stream
 * and the JSONL parser in lms_info/training_generator.py):
 *   {type: "started", job: {...}}             — first event, carries job_uuid
 *   {type: "program", name: "..."}            — overall outline title
 *   {type: "module",  module_index: i, name: "..."}
 *   {type: "section", module_index: i, section: {name, brief,
 *                                                 suggest_swipe_deck,
 *                                                 suggest_quiz}}
 *   {type: "done",    job: {...}}             — final, carries persisted outline_json
 *   {type: "error",   text: "..."}            — terminal failure
 *
 * @param {string} url       - Endpoint URL (API_post_learning_generation_job_stream)
 * @param {object} params    - Request body (sent as JSON):
 *                             {company_id, target_kind, prompt, source_file_uuids[],
 *                              role_ids[] (full_lms only)}
 * @param {object} callbacks - { onStarted(job), onProgram(name),
 *                              onModule(name, moduleIndex),
 *                              onSection(section, moduleIndex),
 *                              onTrack(track, trackIndex)  // full_lms only
 *                              onDone(job), onError(msg) }
 * @param {object} options   - { signal?: AbortSignal } — see
 *                             streamQuizGenerationAPI for details. Aborting
 *                             closes the connection; the backend's
 *                             GeneratorExit handler then marks the job
 *                             FAILED so a retry creates a fresh row.
 * Resolves on `done`; rejects on network/HTTP errors, `error` events, or abort.
 */
export async function streamOutlineGenerationAPI(url, params, callbacks, options = {}) {
  const {
    onStarted, onProgram, onModule, onSection, onTrack, onDone, onError,
  } = callbacks || {};
  const { signal } = options;
  const token = getAuthToken();

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Token ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
    signal,
  });

  if (!response.ok) {
    throw await _readErrorEnvelope(response);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let receivedDone = false;

  while (true) {
    if (signal?.aborted) {
      try { await reader.cancel(); } catch (_) { /* noop */ }
      const abortErr = new Error("Aborted");
      abortErr.name = "AbortError";
      throw abortErr;
    }

    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop();

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const jsonStr = line.slice(6).trim();
      if (!jsonStr) continue;

      let event;
      try {
        event = JSON.parse(jsonStr);
      } catch (parseErr) {
        continue;
      }

      if (event.type === "started") {
        onStarted?.(event.job);
      } else if (event.type === "program") {
        onProgram?.(event.name);
      } else if (event.type === "module") {
        onModule?.(event.name, event.module_index);
      } else if (event.type === "section") {
        onSection?.(event.section, event.module_index);
      } else if (event.type === "track") {
        // Full-LMS only — carries name, role_names, module_indices.
        // Single-track / module flows never emit this event so the
        // callback is optional and silently no-ops if absent.
        onTrack?.(
          {
            name: event.name,
            role_names: event.role_names || [],
            module_indices: event.module_indices || [],
          },
          event.track_index,
        );
      } else if (event.type === "done") {
        receivedDone = true;
        onDone?.(event.job);
      } else if (event.type === "error") {
        const errorMsg = event.text || "An unknown error occurred.";
        onError?.(errorMsg);
        throw new Error(errorMsg);
      }
    }
  }

  if (!receivedDone) {
    // See streamQuizGenerationAPI for rationale — toasted verbatim.
    throw new Error("Generation was interrupted. Please try again.");
  }
}

/**
 * Stream an SSE response from the outline-edit endpoint. Manager submits a
 * natural-language edit prompt; the backend re-emits the full outline using
 * the same event vocabulary as streamOutlineGenerationAPI. Module + section
 * events may carry an additional `handle` field — that's a server-internal
 * identity tag the FE doesn't need to track; the canonical post-edit
 * outline_json (with real uuids) arrives in the terminal `done` event and
 * is what the caller should merge into local state.
 *
 * @param {string} url       - API_post_learning_generation_outline_edit_stream
 * @param {object} params    - { company_id, job_uuid, edit_prompt }
 * @param {object} callbacks - same shape as streamOutlineGenerationAPI:
 *                             { onStarted, onProgram, onModule, onSection,
 *                               onTrack (full_lms only), onDone, onError }
 * @param {object} options   - { signal?: AbortSignal }
 *
 * Resolves on `done`; rejects on network/HTTP errors, `error` events, or
 * abort. Failure leaves the job in AWAITING_APPROVAL with outline_json
 * unchanged — safe to retry.
 *
 * Full-LMS edit nuance: in edit mode track events carry `module_handles`
 * (strings like "M0") rather than `module_indices` (integers), so the
 * onTrack callback receives both fields and the caller picks whichever
 * is present.
 */
export async function streamOutlineEditAPI(url, params, callbacks, options = {}) {
  const {
    onStarted, onProgram, onModule, onSection, onTrack, onDone, onError,
  } = callbacks || {};
  const { signal } = options;
  const token = getAuthToken();

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Token ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
    signal,
  });

  if (!response.ok) {
    throw await _readErrorEnvelope(response);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let receivedDone = false;

  while (true) {
    if (signal?.aborted) {
      try { await reader.cancel(); } catch (_) { /* noop */ }
      const abortErr = new Error("Aborted");
      abortErr.name = "AbortError";
      throw abortErr;
    }

    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop();

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const jsonStr = line.slice(6).trim();
      if (!jsonStr) continue;

      let event;
      try {
        event = JSON.parse(jsonStr);
      } catch (parseErr) {
        continue;
      }

      if (event.type === "started") {
        onStarted?.(event.job);
      } else if (event.type === "program") {
        onProgram?.(event.name);
      } else if (event.type === "module") {
        onModule?.(event.name, event.module_index);
      } else if (event.type === "section") {
        onSection?.(event.section, event.module_index);
      } else if (event.type === "track") {
        // Full-LMS edit: track events carry module_handles (strings)
        // in edit mode vs module_indices (integers) in fresh mode.
        // The terminal `done` event carries the canonical post-edit
        // outline_json with real uuids — that's what the caller
        // should merge — so this callback is primarily for live
        // streaming UX rather than persistence.
        onTrack?.(
          {
            name: event.name,
            role_names: event.role_names || [],
            module_handles: event.module_handles || null,
            module_indices: event.module_indices || null,
          },
          event.track_index,
        );
      } else if (event.type === "done") {
        receivedDone = true;
        onDone?.(event.job);
      } else if (event.type === "error") {
        const errorMsg = event.text || "An unknown error occurred.";
        onError?.(errorMsg);
        throw new Error(errorMsg);
      }
    }
  }

  if (!receivedDone) {
    throw new Error("Edit was interrupted. Please try again.");
  }
}

/**
 * Stream an SSE response from the AI Reporting endpoint via POST.
 *
 * Handles additional event types (thinking, chart_data) beyond
 * the standard token/done/error used by streamRightworkAPI.
 *
 * @param {string} url        - Endpoint URL
 * @param {object} params     - Request body (sent as JSON)
 * @param {object} callbacks  - Event handlers:
 *   onToken(text), onChartData(data), onThinking(text), onDone(payload), onError(msg)
 */
export async function streamAIReportingAPI(url, params, callbacks) {
  const { onToken, onChartData, onThinking, onDone, onError } = callbacks;
  const token = getAuthToken();

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Token ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const err = new Error(`Request failed with status ${response.status}`);
    err.status = response.status;
    throw err;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let receivedDone = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop();

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const jsonStr = line.slice(6).trim();
      if (!jsonStr) continue;

      let event;
      try {
        event = JSON.parse(jsonStr);
      } catch (parseErr) {
        continue;
      }

      if (event.type === "token") {
        onToken?.(event.text);
      } else if (event.type === "thinking") {
        onThinking?.(event.text);
      } else if (event.type === "chart_data") {
        onChartData?.(event.data);
      } else if (event.type === "done") {
        receivedDone = true;
        onDone?.(event);
      } else if (event.type === "error") {
        const errorMsg = event.text || "An unknown error occurred.";
        onError?.(errorMsg);
        throw new Error(errorMsg);
      }
    }
  }

  if (!receivedDone) {
    throw new Error("Stream ended unexpectedly without completing.");
  }
}
